import React, { useState, useMemo, useEffect } from "react";
import { Formik, Form } from "formik";
import {
  RatingContainer,
  RatingLabel,
  StyledDialog,
  DialogTitleStyled,
  DialogContentStyled,
  DialogActionsStyled,
  CancelButton,
  DoneButton,
  EmployeeAvatar,
  CompanyAvatar,
  AvatarRow,
} from "./employeeReview.styles";
import { Review } from "../../../interfaces/entities/review";
import { getReviewValidationSchema } from "../../../validationSchema/reviewValidationSchema";
import { TextInput } from "../../shared/textInput/textInput";
import { RatingInput } from "../../shared/ratingInput/ratingInput";
import { Company } from "../../../interfaces/entities/company";

interface ReviewProps {
  initialValues: Review;
  userId?: string;
  employeeName?: string;
  employeeImage?: string;
  company: Company;
  onReviewSubmit: (review: Review) => void;
  openDialog?: boolean;
  onDialogClose?: () => void;
}

export const EmployeeReview: React.FC<ReviewProps> = ({
  initialValues,
  onReviewSubmit,
  employeeName,
  employeeImage,
  company,
  openDialog,
  onDialogClose,
}) => {
  const [showMessageDialog, setShowMessageDialog] = useState(false);

  useEffect(() => {
    if (openDialog) {
      setShowMessageDialog(true);
    }
  }, [openDialog]);

  // Get the phoneMandatoryForReview value from company config
  const phoneMandatoryForReview = company?.config?.phoneMandatoryForReview ?? false;

  // Create dynamic validation schema based on company setting
  const validationSchema = useMemo(
    () => getReviewValidationSchema(phoneMandatoryForReview),
    [phoneMandatoryForReview]
  );

  const getRatingLabel = (value: number): string => {
    const labels = ["", "Bad", "Fair", "Good", "Very Good", "Excellent"];
    return labels[value] || "";
  };

  const handleCancelDialog = () => {
    setShowMessageDialog(false);
    onDialogClose?.();
  };

  const handleSubmit = (values: Review) => {
    onReviewSubmit(values);
    setShowMessageDialog(false);
    onDialogClose?.();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, dirty }) => (
        <>
          <RatingContainer>
            <RatingInput onChange={() => { setShowMessageDialog(true) }} showHint={false} />
          </RatingContainer>

          <StyledDialog
            open={showMessageDialog}
            onClose={handleCancelDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitleStyled>
              How was your experience with {employeeName || "our staff"} at {company.name}
            </DialogTitleStyled>

            <Form>
              <DialogContentStyled>
                {(employeeImage || company?.image) && (
                  <AvatarRow>

                    {company?.image && (
                      <CompanyAvatar
                        src={company.image}
                        alt={company.name || "Company"}
                      />
                    )}
                    {employeeImage && (
                      <EmployeeAvatar
                        src={employeeImage}
                        alt={employeeName || "Employee"}
                      />
                    )}
                  </AvatarRow>
                )}

                <RatingContainer >
                  <RatingInput />
                  {values.rating > 0 && (
                    <RatingLabel variant="body2" >
                      {getRatingLabel(values.rating)}
                    </RatingLabel>
                  )}
                </RatingContainer>

                <TextInput
                  label="Let us know about your experience"
                  name="comment"
                  errorText={errors.comment && touched.comment ? errors.comment : ""}
                  multiline
                  rows={4}
                />
                <TextInput
                  label="Your Phone Number"
                  name="reviewerPhone"
                  errorText={errors.reviewerPhone && touched.reviewerPhone ? errors.reviewerPhone : ""}
                />
              </DialogContentStyled>

              <DialogActionsStyled>
                <CancelButton
                  type="button"
                  onClick={handleCancelDialog}
                >
                  Cancel
                </CancelButton>
                <DoneButton
                  type="submit"
                  variant="contained"
                  disabled={!isValid}
                >
                  Submit
                </DoneButton>
              </DialogActionsStyled>
            </Form>
          </StyledDialog>   </>)}
    </Formik>

  );
};

